import React, { useState, useEffect, KeyboardEvent } from 'react';
import { Save, X, Trash2, FilePlus2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { captureSupabaseFailure } from '../../sentry';

type ContentStubRow = {
  id: string;
  content_id: string;
  title: string;
  status: string;
  scheduled_for: string | null;
  pub_date: string | null;
};

function slugifyStub(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

interface Thought {
  id: string;
  title: string;
  seed_idea: string;
  reference: string;
  status: string;
  final_copy: string;
  arc_slug: string | null;
  arc_stage: string | null;
  arc_placement: string | null;
  arc_fit_note: string | null;
  first_published_at: string | null;
  created_at: string;
  updated_at: string;
}

interface ArcOption {
  slug: string;
  label: string;
}

const ARC_STAGES = [
  { value: '', label: 'Unassigned' },
  { value: 'setup', label: 'Setup' },
  { value: 'complication', label: 'Complication' },
  { value: 'resolution', label: 'Resolution' },
] as const;

const ARC_PLACEMENTS = [
  { value: 'suggested', label: 'Suggested (ghost)' },
  { value: 'tentative', label: 'Tentative' },
  { value: 'locked', label: 'Locked' },
] as const;

interface ThoughtModalProps {
  isOpen: boolean;
  onClose: () => void;
  thought: Thought | null;
  /** Prefill when creating a new thought (e.g. arc from detail page). */
  seed?: Partial<Thought> | null;
  onSuccess: () => void;
  /**
   * `arc` — compact sheet for narrative canvas (arc/stage live on the board;
   * convert-to-content lives on the timeline).
   * `full` — thoughts list editor with all fields.
   */
  variant?: 'full' | 'arc';
}

const ThoughtModal: React.FC<ThoughtModalProps> = ({
  isOpen,
  onClose,
  thought,
  seed = null,
  onSuccess,
  variant = 'full',
}) => {
  const { user } = useAuth();
  const isArc = variant === 'arc';
  const [formData, setFormData] = useState<Partial<Thought>>({
    title: '',
    seed_idea: '',
    reference: '',
    status: 'draft',
    final_copy: '',
    arc_slug: null,
    arc_stage: null,
    arc_placement: null,
    arc_fit_note: null,
    first_published_at: null,
  });
  const [arcs, setArcs] = useState<ArcOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [stubs, setStubs] = useState<ContentStubRow[]>([]);
  const [stubTitles, setStubTitles] = useState('');
  const [stubFirstDate, setStubFirstDate] = useState(() =>
    addDaysIso(new Date().toISOString().slice(0, 10), 14),
  );
  const [stubSpacing, setStubSpacing] = useState(7);
  const [isStubbing, setIsStubbing] = useState(false);
  const isNewThought = !thought;

  useEffect(() => {
    if (thought) {
      setFormData({
        ...thought,
        first_published_at: thought.first_published_at
          ? String(thought.first_published_at).slice(0, 10)
          : null,
      });
    } else {
      setFormData({
        title: '',
        seed_idea: '',
        reference: '',
        status: 'draft',
        final_copy: '',
        arc_slug: null,
        arc_stage: null,
        arc_placement: null,
        arc_fit_note: null,
        first_published_at: null,
        ...seed,
      });
    }
  }, [thought, seed]);

  useEffect(() => {
    let cancelled = false;
    async function loadArcs() {
      const { data: board } = await supabase.from('system_maps').select('id').eq('slug', 'narrative-arcs').maybeSingle();
      if (!board?.id || cancelled) return;
      const { data } = await supabase
        .from('map_nodes')
        .select('slug, label')
        .eq('map_id', board.id)
        .order('y', { ascending: true });
      if (!cancelled) setArcs((data ?? []) as ArcOption[]);
    }
    void loadArcs();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadStubs() {
      if (isArc || !thought?.id) {
        setStubs([]);
        setStubTitles('');
        return;
      }
      setStubTitles(thought.title?.trim() || '');
      const { data, error } = await supabase
        .from('site_content')
        .select('id, content_id, title, status, scheduled_for, pub_date')
        .eq('source_thought_id', thought.id)
        .order('scheduled_for', { ascending: true });
      if (error) {
        console.error(error);
        return;
      }
      if (!cancelled) setStubs((data ?? []) as ContentStubRow[]);
    }
    void loadStubs();
    return () => {
      cancelled = true;
    };
  }, [thought?.id, thought?.title, isOpen, isArc]);

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscKey as any);
    return () => {
      document.removeEventListener('keydown', handleEscKey as any);
    };
  }, [isOpen, onClose, showDeleteConfirm]);

  const handleSave = async () => {
    if (!formData.title?.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setIsSaving(true);
    try {
      const arcSlug = formData.arc_slug?.trim() || null;
      const payload = {
        ...formData,
        arc_slug: arcSlug,
        arc_stage: arcSlug ? formData.arc_stage?.trim() || null : null,
        arc_placement: arcSlug
          ? formData.arc_placement?.trim() || 'tentative'
          : null,
        arc_fit_note: arcSlug
          ? formData.arc_fit_note?.trim() || null
          : null,
        first_published_at: formData.first_published_at?.trim() || null,
      };

      if (isNewThought) {
        const { error } = await supabase.from('thoughts').insert([
          {
            ...payload,
            embedding_status: 'needs_embedding',
          },
        ]);

        if (error) throw error;
        toast.success('Thought created successfully');
      } else {
        const { error } = await supabase
          .from('thoughts')
          .update({
            ...payload,
            updated_at: new Date().toISOString(),
          })
          .eq('id', thought!.id);

        if (error) throw error;
        toast.success('Thought updated successfully');
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      captureSupabaseFailure('thoughts', isNewThought ? 'create' : 'update', error);
      toast.error(`Error saving thought: ${error.message}`);
      console.error('Save thought error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!thought) return;

    setIsDeleting(true);
    try {
      const { error } = await supabase.from('thoughts').delete().eq('id', thought.id);

      if (error) throw error;

      toast.success('Thought deleted successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      captureSupabaseFailure('thoughts', 'delete', error, { thought_id: thought.id });
      toast.error(`Error deleting thought: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleCreateStubs = async () => {
    if (!thought) return;
    const titles = stubTitles
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean);
    if (titles.length === 0) {
      toast.error('Add at least one content title (one per line)');
      return;
    }

    setIsStubbing(true);
    try {
      const now = new Date().toISOString();
      const rows = titles.map((title, i) => {
        const content_id = `${slugifyStub(title) || 'stub'}-${thought.id}-${Date.now().toString(36)}-${i}`;
        return {
          collection: 'insights',
          content_id,
          title,
          url: '',
          body_hash: `engine_stub:${content_id}`,
          status: 'draft',
          origin: 'engine_stub',
          scheduled_for: stubFirstDate
            ? addDaysIso(stubFirstDate, stubSpacing * i)
            : null,
          pub_date: null,
          maturity: 0,
          gated: false,
          arc_slug: formData.arc_slug ?? thought.arc_slug,
          arc_stage: formData.arc_stage ?? thought.arc_stage ?? 'complication',
          source_thought_id: Number(thought.id),
          created_at: now,
          updated_at: now,
        };
      });

      const { data, error } = await supabase.from('site_content').insert(rows).select(
        'id, content_id, title, status, scheduled_for, pub_date',
      );
      if (error) throw error;

      setStubs((prev) => [...prev, ...((data ?? []) as ContentStubRow[])]);
      toast.success(
        titles.length === 1
          ? 'Content stub created'
          : `${titles.length} content stubs created`,
      );
      onSuccess();
    } catch (error: any) {
      captureSupabaseFailure('site_content', 'create', error, { thought_id: thought.id });
      toast.error(`Could not create stubs: ${error.message}`);
    } finally {
      setIsStubbing(false);
    }
  };

  if (!isOpen) return null;

  const field =
    'w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500';
  const label = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';
  const arcLabel =
    arcs.find((a) => a.slug === formData.arc_slug)?.label ?? formData.arc_slug ?? 'Unassigned';
  const stageLabel =
    ARC_STAGES.find((s) => s.value === (formData.arc_stage || ''))?.label ?? 'Unstaged';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-lg w-full max-w-lg max-h-[85vh] sm:max-h-[80vh] shadow-xl flex flex-col">
        <div className="shrink-0 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {isNewThought ? 'New idea' : 'Idea'}
            </h2>
            {isArc && formData.arc_slug && (
              <p className="text-[11px] font-mono text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                {arcLabel} · {stageLabel}
                <span className="text-gray-300 dark:text-gray-600"> · drag on board to move stage</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div>
            <label className={label}>Title</label>
            <input
              type="text"
              autoFocus
              className={field}
              value={formData.title || ''}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Idea title"
            />
          </div>

          <div>
            <label className={label}>Seed</label>
            <textarea
              className={`${field} min-h-[64px] max-h-[140px]`}
              value={formData.seed_idea || ''}
              onChange={(e) => setFormData({ ...formData, seed_idea: e.target.value })}
              placeholder="Short abstract / seed"
              rows={isArc ? 3 : 4}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Status</label>
              <select
                className={field}
                value={formData.status || 'draft'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="draft">Draft</option>
                <option value="in_progress">In Progress</option>
                <option value="promoted">Promoted</option>
                <option value="completed">Completed</option>
                <option value="dead">Dead</option>
              </select>
            </div>
            <div>
              <label className={label}>Placement</label>
              <select
                className={field}
                value={formData.arc_placement || ''}
                disabled={!formData.arc_slug}
                onChange={(e) =>
                  setFormData({ ...formData, arc_placement: e.target.value || null })
                }
              >
                {ARC_PLACEMENTS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isArc && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500 -mt-1">
              Schedule lives on content stubs on the timeline — not on the idea.
            </p>
          )}

          {!isArc && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Arc</label>
                <select
                  className={field}
                  value={formData.arc_slug || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      arc_slug: e.target.value || null,
                      arc_stage: e.target.value ? formData.arc_stage || 'complication' : null,
                      arc_placement: e.target.value
                        ? formData.arc_placement || 'tentative'
                        : null,
                    })
                  }
                >
                  <option value="">Unassigned</option>
                  {arcs.map((arc) => (
                    <option key={arc.slug} value={arc.slug}>
                      {arc.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={label}>Stage</label>
                <select
                  className={field}
                  value={formData.arc_stage || ''}
                  disabled={!formData.arc_slug}
                  onChange={(e) =>
                    setFormData({ ...formData, arc_stage: e.target.value || null })
                  }
                >
                  {ARC_STAGES.map((s) => (
                    <option key={s.value || 'none'} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <details className="group rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2">
            <summary className="text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer select-none list-none flex items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
              <span>
                Why here
                {formData.arc_fit_note?.trim() ? (
                  <span className="ml-1.5 text-gray-300 dark:text-gray-600 font-normal">· set</span>
                ) : null}
              </span>
              <span className="text-gray-300 dark:text-gray-600 group-open:rotate-90 transition-transform">
                ›
              </span>
            </summary>
            <div className="mt-2">
              <textarea
                className={`${field} min-h-[56px]`}
                value={formData.arc_fit_note || ''}
                disabled={!formData.arc_slug}
                onChange={(e) =>
                  setFormData({ ...formData, arc_fit_note: e.target.value || null })
                }
                placeholder="Why this idea sits on this arc / stage"
              />
            </div>
          </details>

          <details className="group rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2">
            <summary className="text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer select-none list-none flex items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
              <span>Reference & notes</span>
              <span className="text-gray-300 dark:text-gray-600 group-open:rotate-90 transition-transform">
                ›
              </span>
            </summary>
            <div className="mt-2 space-y-2">
              <textarea
                className={`${field} min-h-[56px]`}
                value={formData.reference || ''}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                placeholder="Reference URLs / notes"
              />
              <textarea
                className={`${field} min-h-[56px]`}
                value={formData.final_copy || ''}
                onChange={(e) => setFormData({ ...formData, final_copy: e.target.value })}
                placeholder="Final copy (optional — writing lives on content stubs)"
              />
            </div>
          </details>

          {!isArc && !isNewThought && (
            <details className="group rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2">
              <summary className="text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer select-none list-none flex items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
                <span className="inline-flex items-center gap-1.5">
                  <FilePlus2 className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                  Convert to content
                  {stubs.length > 0 ? (
                    <span className="font-mono text-[10px] text-gray-400">· {stubs.length}</span>
                  ) : null}
                </span>
                <span className="text-gray-300 dark:text-gray-600 group-open:rotate-90 transition-transform">
                  ›
                </span>
              </summary>
              <div className="mt-2 space-y-2">
                {stubs.length > 0 && (
                  <ul className="space-y-1">
                    {stubs.map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center justify-between gap-2 text-[12px] text-gray-700 dark:text-gray-300"
                      >
                        <span className="truncate min-w-0">
                          <span
                            className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${
                              s.status === 'published'
                                ? 'bg-blue-600'
                                : 'border border-blue-400 bg-transparent'
                            }`}
                          />
                          {s.title}
                        </span>
                        <span className="font-mono text-[10px] text-gray-400 shrink-0">
                          {s.status === 'published'
                            ? s.pub_date
                            : s.scheduled_for
                              ? `draft · ${s.scheduled_for}`
                              : 'draft'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                <textarea
                  className={`${field} min-h-[56px]`}
                  value={stubTitles}
                  onChange={(e) => setStubTitles(e.target.value)}
                  placeholder="One content title per line"
                />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={label}>First date</label>
                    <input
                      type="date"
                      className={field}
                      value={stubFirstDate}
                      onChange={(e) => setStubFirstDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={label}>Days between</label>
                    <input
                      type="number"
                      min={1}
                      max={60}
                      className={field}
                      value={stubSpacing}
                      onChange={(e) => setStubSpacing(Number(e.target.value) || 7)}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void handleCreateStubs()}
                  disabled={isStubbing}
                  className="inline-flex items-center px-2.5 py-1.5 text-xs rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
                >
                  <FilePlus2 className="w-3.5 h-3.5 mr-1.5" />
                  {isStubbing ? 'Creating…' : 'Create stubs'}
                </button>
              </div>
            </details>
          )}

          {isArc && (
            <p className="text-[11px] text-gray-400 dark:text-gray-500">
              Add content from the timeline · move stage by dragging on the board
            </p>
          )}
        </div>

        <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 px-4 py-2.5 flex items-center justify-between gap-2">
          <div>
            {!isNewThought && !showDeleteConfirm && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="inline-flex items-center px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                disabled={isDeleting}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Delete
              </button>
            )}
            {showDeleteConfirm && (
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-gray-500">Sure?</span>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-2 py-1 bg-red-600 text-white rounded"
                  disabled={isDeleting}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded"
                >
                  No
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="inline-flex items-center px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50"
              disabled={isSaving}
            >
              <Save className="w-3.5 h-3.5 mr-1" />
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThoughtModal;
